import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSaveUnitResults } from "@repo/edtech-api";

import {
  LessonsLibrary,
  useSyncSearchParams,
} from "../../../lib";
import { CURRENT_LESSON_INDEX_KEY } from "../../constants";
import {
  ExtendedLesson,
  LessonStatus,
  LessonType,
  SaveUnitResult,
} from "../../types";
import { useIdContext } from "../id-provider";
import { MainContextActions, MainContextValues } from "./main-context";

const START_INDEX = 1;

const getInit = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const urlIndex = params.get(CURRENT_LESSON_INDEX_KEY);
  return urlIndex && Number(urlIndex) > 0 ?
    Number(urlIndex) :
    START_INDEX;
};

type Props = {
  lessonNodeIds: string[],
  library: LessonsLibrary,
  setLibrary: (lessons: LessonsLibrary) => void,
  children: ReactNode,
  isLessonsLoading: boolean,
  rootNodeIds: string[],
  unitsChildren: Record<string, string[]>,
  isCoursePassed: boolean,
  isCoursePassedByBackend: boolean,
  hasSuccessScreen: boolean,
  isAutomaticFinishLesson: boolean,
};

export const MainProvider: FC<Props> = ({
  lessonNodeIds,
  library,
  setLibrary,
  children,
  isLessonsLoading,
  rootNodeIds,
  unitsChildren,
  isCoursePassed,
  isCoursePassedByBackend,
  hasSuccessScreen,
  isAutomaticFinishLesson,
}) => {
  const [ currentLessonIndex, setCurrentLessonIndex ] = useState(getInit);
  const { trigger } = useSaveUnitResults<SaveUnitResult>();
  const {
    publicationId,
    unitId,
  } = useIdContext();

  const currentLesson = useMemo(() => library[ lessonNodeIds[ currentLessonIndex - 1 ]! ] || null,
    [
      currentLessonIndex,
      lessonNodeIds,
      library,
    ]);

  const prevLesson = useMemo(() => library[ lessonNodeIds[ currentLessonIndex - 2 ]! ] || null,
    [
      currentLessonIndex,
      lessonNodeIds,
      library,
    ]);

  const nextLesson = useMemo(() => library[ lessonNodeIds[ currentLessonIndex ]! ] || null,
    [
      currentLessonIndex,
      lessonNodeIds,
      library,
    ]);

  const isLastLesson = useMemo(() => currentLessonIndex === lessonNodeIds?.length,
    [ currentLessonIndex, lessonNodeIds?.length ]);

  const isLessonAvailable = useCallback((index: number): boolean => {
    if (index < START_INDEX || index > lessonNodeIds.length) return false;
    const lesson = library[ lessonNodeIds[ index - 1 ]! ];
    if (!lesson) return false;
    if (lesson.hideCard) return false;

    return lesson.isParentAvailable !== false;
  }, [ lessonNodeIds, library ]);

  const findNextAvailableLesson = useCallback((fromIndex: number): number => {
    for (let i = fromIndex; i <= lessonNodeIds.length; i++) {
      if (isLessonAvailable(i)) {
        return i;
      }
    }
    return -1;
  }, [ lessonNodeIds.length, isLessonAvailable ]);

  const findPrevAvailableLesson = useCallback((fromIndex: number): number => {
    for (let i = fromIndex; i >= START_INDEX; i--) {
      if (isLessonAvailable(i)) {
        return i;
      }
    }
    return -1;
  }, [ isLessonAvailable ]);

  const findNearestAvailableLesson = useCallback((targetIndex: number): number => {
    if (isLessonAvailable(targetIndex)) {
      return targetIndex;
    }

    let distance = 1;
    const maxDistance = Math.max(targetIndex - START_INDEX, lessonNodeIds.length - targetIndex);

    while (distance <= maxDistance) {
      const prevIndex = targetIndex - distance;
      if (prevIndex >= START_INDEX && isLessonAvailable(prevIndex)) {
        return prevIndex;
      }

      const nextIndex = targetIndex + distance;
      if (nextIndex <= lessonNodeIds.length && isLessonAvailable(nextIndex)) {
        return nextIndex;
      }

      distance++;
    }

    return findNextAvailableLesson(START_INDEX);
  }, [
    isLessonAvailable,
    lessonNodeIds.length,
    findNextAvailableLesson,
  ]);

  const isPrevLessonAvailable = useMemo(() => {
    if (currentLessonIndex <= START_INDEX) return false;
    const prevAvailableIndex = findPrevAvailableLesson(currentLessonIndex - 1);
    return prevAvailableIndex !== -1;
  }, [ currentLessonIndex, findPrevAvailableLesson ]);

  const isNextLessonAvailable = useMemo(() => {
    if (currentLessonIndex >= lessonNodeIds.length) return false;
    const nextAvailableIndex = findNextAvailableLesson(currentLessonIndex + 1);
    return nextAvailableIndex !== -1;
  }, [
    currentLessonIndex,
    lessonNodeIds.length,
    findNextAvailableLesson,
  ]);

  const prevLessonAction = useCallback(() => {
    const prev = findPrevAvailableLesson(currentLessonIndex - 1);
    if (prev !== -1) {
      setCurrentLessonIndex(prev);
    }
  }, [ currentLessonIndex, findPrevAvailableLesson ]);

  const nextLessonAction = useCallback(() => {
    const next = findNextAvailableLesson(currentLessonIndex + 1);
    if (next !== -1) {
      setCurrentLessonIndex(next);
    }
  }, [ currentLessonIndex, findNextAvailableLesson ]);

  const setLessonAction = useCallback((index: number) => {
    const safeIndex = findNearestAvailableLesson(index);
    setCurrentLessonIndex(safeIndex);
  }, [ findNearestAvailableLesson ]);

  useSyncSearchParams({
    localState: currentLessonIndex,
    changeLocalStateAction: (newIndex: string) => {
      const targetIndex = Number(newIndex);
      if (targetIndex > 0) {
        setLessonAction(targetIndex);
      }
    },
  });

  useEffect(() => {
    if (!lessonNodeIds.length || isLessonsLoading) return;

    const lessonsLength = lessonNodeIds?.length;

    if (currentLessonIndex > lessonsLength ||
      currentLesson?.hideCard ||
      !currentLesson?.isParentAvailable) {
      const safeIndex = findNearestAvailableLesson(currentLessonIndex);
      setCurrentLessonIndex(safeIndex);
    }
  }, [
    currentLesson?.hideCard,
    currentLesson?.isParentAvailable,
    currentLessonIndex,
    isLessonsLoading,
    lessonNodeIds.length,
    findNearestAvailableLesson,
    currentLesson,
  ]);

  const hasNestedUnits = useMemo(() => {
    return Object.keys(unitsChildren).length > 0;
  }, [ unitsChildren ]);

  const currentUnitNodeId = useMemo(() => {
    if (!currentLesson) return "";
    const parentNodeIds = currentLesson.parentNodeIds ?? [];
    return parentNodeIds[ parentNodeIds.length - 1 ] ?? currentLesson.nodeId;
  }, [ currentLesson ]);

  const isCurrentUnitPassed = useMemo(() => {
    if (!currentUnitNodeId) return false;
    return library[ currentUnitNodeId ]?.isFinished === true;
  }, [ currentUnitNodeId, library ]);

  const isAllLessonsInUnitPassed = useMemo(() => {
    if (!currentUnitNodeId) return false;

    const getLeafNodeIds = (nodeId: string): string[] => {
      const children = unitsChildren[ nodeId ] ?? [];

      if (children.length === 0) return [ nodeId ];

      return children.flatMap((childNodeId) => getLeafNodeIds(childNodeId));
    };

    return getLeafNodeIds(currentUnitNodeId)
      .every((nodeId) => library[ nodeId ]?.isFinished === true);
  }, [
    currentUnitNodeId,
    unitsChildren,
    library,
  ]);

  const areAllChildrenUnitsOfCurrentPassed = useMemo(() => {
    if (!currentUnitNodeId) return true;

    const children = unitsChildren[ currentUnitNodeId ] ?? [];
    if (children.length === 0) return true;
    return children.every((nodeId) => library[ nodeId ]?.isFinished === true);
  }, [
    currentUnitNodeId,
    unitsChildren,
    library,
  ]);

  const firstUnpassedParentUnitId = useMemo(() => {
    if (!currentUnitNodeId) return null;

    const parentChain = library[ currentUnitNodeId ]?.parentNodeIds ?? [];

    for (let index = parentChain.length - 1; index >= 0; index--) {
      const parentNodeId = parentChain[ index ]!;

      if (library[ parentNodeId ]?.isFinished !== true) {
        return parentNodeId;
      }
    }

    return null;
  }, [ currentUnitNodeId, library ]);

  const isParentUnitPassed = useMemo(() => {
    return !firstUnpassedParentUnitId;
  }, [ firstUnpassedParentUnitId ]);

  const areAllChildrenOfParentPassed = useMemo(() => {
    if (!firstUnpassedParentUnitId) return false;
    const children = unitsChildren[ firstUnpassedParentUnitId ] ?? [];
    if (children.length === 0) return false;
    return children.every((nodeId) => library[ nodeId ]?.isFinished === true);
  }, [
    firstUnpassedParentUnitId,
    unitsChildren,
    library,
  ]);

  const isAllLessonsInCoursePassed = useMemo(() => {
    return lessonNodeIds.every((nodeId) => library[ nodeId ]?.isFinished === true);
  }, [ lessonNodeIds, library ]);

  const areAllUnitsPassed = useMemo(() => {
    if (!hasNestedUnits) return true;
    return Object.keys(unitsChildren).every((nodeId) => {
      const children = unitsChildren[ nodeId ] ?? [];
      const isEmpty = children.length === 0;
      if (isEmpty) return true;
      return library[ nodeId ]?.isFinished === true;
    });
  }, [
    hasNestedUnits,
    unitsChildren,
    library,
  ]);

  useEffect(() => {
    if (!currentLesson) return;

    const isUnitReadyToSave = !!currentLesson.unitid && hasNestedUnits && !isCurrentUnitPassed && isAllLessonsInUnitPassed && areAllChildrenUnitsOfCurrentPassed;
    const isParentUnitReadyToSave = !!currentLesson.unitid && hasNestedUnits && !isParentUnitPassed && areAllChildrenOfParentPassed;
    const isCourseReadyToSave = !isCoursePassedByBackend && isAllLessonsInCoursePassed && areAllUnitsPassed;

    if (isUnitReadyToSave) {
      trigger({
        publicationId,
        unitId: currentLesson?.unitid as string,
        mainUnitId: unitId,
      });
    } else if (isParentUnitReadyToSave) {
      trigger({
        publicationId,
        unitId: library[ firstUnpassedParentUnitId as string ]?.uid as string,
        mainUnitId: unitId,
      });
    } else if (isCourseReadyToSave) {
      trigger({
        publicationId,
        unitId: unitId as string,
        mainUnitId: unitId,
      });
    }
  }, [
    currentLesson?.unitid,
    firstUnpassedParentUnitId,
    hasNestedUnits,
    isCoursePassedByBackend,
    isCurrentUnitPassed,
    isParentUnitPassed,
    isAllLessonsInUnitPassed,
    areAllChildrenUnitsOfCurrentPassed,
    areAllChildrenOfParentPassed,
    isAllLessonsInCoursePassed,
    areAllUnitsPassed,
    library,
    publicationId,
    trigger,
    unitId,
  ]);

  const updateLibraryAction = useCallback((
    nodeId: string,
    lessonData: ExtendedLesson | Partial<ExtendedLesson>,
    overwrite: boolean = false,
  ) => {
    const current = library[ nodeId ];

    if (overwrite) {
      setLibrary({
        ...library,
        [ nodeId ]: lessonData as ExtendedLesson,
      });
      return;
    }

    setLibrary({
      ...library,
      [ nodeId ]: {
        ...(current ?? {}),
        ...(lessonData as Partial<ExtendedLesson>),
      } as ExtendedLesson,
    });
  }, [ library, setLibrary ]);

  const showFinishLessonButton = useMemo(() => {
    if (isAutomaticFinishLesson) return false;
    if (!currentLesson) return false;
    if (currentLesson.hideCard) return false;
    if (currentLesson.status === LessonStatus.Passed) return false;
    if (currentLesson.materialType === LessonType.Scorm) return false;
    return currentLesson.materialType !== LessonType.Test;
  }, [ isAutomaticFinishLesson, currentLesson ]);

  const memoValues = useMemo(() => ({
    currentLessonIndex: currentLessonIndex - 1,
    currentLesson,
    prevLesson,
    nextLesson,
    isLastLesson,
    isPrevLessonAvailable,
    isNextLessonAvailable,
    lessonNodeIds,
    library,
    rootNodeIds,
    unitsChildren,
    isCoursePassed,
    hasSuccessScreen,
    showFinishLessonButton,
  }),
  [
    currentLesson,
    currentLessonIndex,
    nextLesson,
    isPrevLessonAvailable,
    isNextLessonAvailable,
    prevLesson,
    isLastLesson,
    lessonNodeIds,
    library,
    rootNodeIds,
    unitsChildren,
    isCoursePassed,
    hasSuccessScreen,
    showFinishLessonButton,
  ]);

  const memoActions = useMemo(() => ({
    nextLessonAction,
    prevLessonAction,
    setLessonAction,
    updateLibraryAction,
  }),
  [
    nextLessonAction,
    prevLessonAction,
    setLessonAction,
    updateLibraryAction,
  ]);

  return (
    <MainContextValues.Provider value={memoValues}>
      <MainContextActions.Provider value={memoActions}>
        {children}
      </MainContextActions.Provider>
    </MainContextValues.Provider>
  );
};
